import logProCheckoutPageViewed from '@alltrails/analytics/events/logProCheckoutPageViewed';
import logProPurchaseBuyNowClicked from '@alltrails/analytics/events/logProPurchaseBuyNowClicked';
import PaymentMethod from '@alltrails/analytics/enums/PaymentMethod';
import WebProPurchaseLocation from '@alltrails/analytics/enums/WebProPurchaseLocation';
import Button from '@alltrails/shared/denali/components/Button';
import proApi from 'api/Pro';
import Link from 'components/Link';
import PaymentFinePrint from 'components/PaymentFinePrint';
import Typography from 'components/Typography';
import useCurrencyFormatter from 'hooks/useCurrencyFormatter';
import useLanguageRegionCode from 'hooks/useLanguageRegionCode';
import activeProBadgeSrc from 'images/icons/cuttlefish/activeProBadge.svg';
import logoSrc from 'images/icons/cuttlefish/logo.svg';
import { useEffect, useState, ReactNode } from 'react';
import { FormattedMessage } from '@alltrails/shared/react-intl';
import Coupon from 'types/Coupon';
import CurrencyCode from 'types/CurrencyCode';
import Plan from 'types/Plan';
import ProResponse from 'types/ProResponse';
import { getPurchaseScreenTypePlan } from 'utils/amplitudeHelper';
import { track } from 'utils/OptimizelyHelpers';
import { handlePaymentFormSuccess } from 'utils/Payment';
import badgeCheckSrc from 'components/icons/badge-check.svg';
import { getPriceTotal, isTrial } from 'utils/PlanUtils';
import { execute } from 'utils/recaptcha_helpers';
import { validate } from 'components/forms/CreditCardForm/CreditCardForm';
import * as styles from './Checkout.module.scss';
import CouponCode from './CouponCode';
import PaymentForm from './PaymentForm';

const features = [
  <FormattedMessage defaultMessage="Download offline maps" />,
  <FormattedMessage defaultMessage="Get alerts for wrong turns" />,
  <FormattedMessage defaultMessage="Unlock more map details" />,
  <FormattedMessage defaultMessage="Print maps for backup" />,
  <FormattedMessage defaultMessage="Bring trails to life with 3D maps" />,
  <FormattedMessage defaultMessage="Explore ad free" />,
  <FormattedMessage defaultMessage="Give back with 1% for the Planet" />
];

// Desktop: https://app.zeplin.io/project/627c18d6c6189e2a52ce6566/screen/627c1bcf26ba802db57c180b
// Mobile: https://app.zeplin.io/project/627c18d6c6189e2a52ce6566/screen/627c241f824169281f996dc1

type CheckoutBaseProps = {
  billedSubheading?: ReactNode;
  billedSubheadingRight?: ReactNode;
  billedToday: ReactNode;
  buttonText: ReactNode;
  coupon?: Coupon;
  currencyCode: CurrencyCode;
  heading: ReactNode;
  plan: Plan;
  recurlyPublicKey: string;
  setCoupon: (arg0: Coupon) => void;
  setPlanToAnnual: () => void;
  subheading?: ReactNode;
  userId: number;
};

function CheckoutBase({
  buttonText,
  billedToday,
  billedSubheading,
  coupon,
  currencyCode,
  heading,
  plan,
  subheading,
  recurlyPublicKey,
  userId,
  billedSubheadingRight,
  setCoupon,
  setPlanToAnnual
}: CheckoutBaseProps) {
  const languageRegionCode = useLanguageRegionCode();
  const [submitting, setSubmitting] = useState(false);

  const [errors, setErrors] = useState<ProResponse['errors']>();

  const [expiration, setExpiration] = useState('');
  const [number, setNumber] = useState('');
  const [cvv, setCvv] = useState('');
  const [postalCode, setPostalCode] = useState('');
  const [name, setName] = useState('');
  const [nameError, setNameError] = useState('');
  const [numberError, setNumberError] = useState<ReactNode>();
  const [cvvError, setCvvError] = useState('');
  const [postalCodeError, setPostalCodeError] = useState();
  const [expirationError, setExpirationError] = useState<ReactNode>();

  async function handleSubmit(e: React.KeyboardEvent | React.FormEvent) {
    e.preventDefault();

    if (coupon?.type !== 'GiftCard') {
      if (
        validate({
          name,
          cvv,
          number,
          expiration,
          postalCode,
          setCvvError,
          setNameError,
          setPostalCodeError,
          setExpirationError,
          setNumberError
        })
      ) {
        return;
      }
    }

    const fullName = name.split(' ');
    const billing_first_name = fullName[0];
    const billing_last_name = fullName[fullName.length - 1];

    const [cc_expiration_month, cc_expiration_year] = expiration.split(' / ');

    try {
      setSubmitting(true);

      const recaptcha = await execute('userPaymentAction');

      const body = {
        recaptcha,
        recurly_plan_code: plan.recurlyPlanCode,
        uid: userId,
        user: {
          user_entered_coupon_code: coupon?.code ?? null,
          cc_number: number,
          cc_expiration_month,
          cc_expiration_year,
          cc_csc: cvv,
          zip: postalCode,
          billing_first_name,
          billing_last_name
        }
      };

      const response = await proApi(body);

      if (response?.errors) {
        setErrors(response.errors);
      } else {
        handlePaymentFormSuccess(
          userId,
          currencyCode,
          coupon,
          plan,
          languageRegionCode,
          coupon?.code,
          WebProPurchaseLocation.ProPage,
          PaymentMethod.CreditCard
        );
      }
    } catch (e) {
      setErrors(e);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <form className={styles.content} onSubmit={handleSubmit}>
      <div className={styles.tryArea}>
        <div className={styles.logo}>
          <img alt="" src={logoSrc} />
          <img alt="" src={activeProBadgeSrc} />
        </div>
        <div className={styles.heading}>{heading}</div>
        {subheading && (
          <div className={styles.subheading}>
            <Typography variant="body3">{subheading}</Typography>
          </div>
        )}
        <ul className={styles.ul}>
          {features.map((feature, index) => (
            <li
              // eslint-disable-next-line react/no-array-index-key
              key={index}
              className={styles.li}
            >
              <img alt="" src={badgeCheckSrc} />
              {feature}
            </li>
          ))}
        </ul>
      </div>
      <div className={styles.hrArea} />
      <div className={styles.billedCopyright}>
        <div className={styles.billedArea}>
          <div className={styles.billed}>
            <div>
              <FormattedMessage defaultMessage="Billed today" />
            </div>
            <div>{billedToday}</div>
          </div>
          <div className={styles.hr} />
          {billedSubheading && (
            <div className={styles.after}>
              <div>{billedSubheading}</div>
              <div>{billedSubheadingRight}</div>
            </div>
          )}
          <div>
            <CouponCode coupon={coupon} setCoupon={setCoupon} setPlanToAnnual={setPlanToAnnual} />
          </div>
        </div>
        <div className={styles.copyright}>
          <FormattedMessage defaultMessage="{year} AllTrails, LLC All Rights Reserved" values={{ year: new Date().getFullYear() }} />
          <br />
          <Link href="/privacy" target="_blank" variant="secondary">
            <FormattedMessage defaultMessage="Privacy Policy" />
          </Link>{' '}
          |{' '}
          <Link href="/terms" target="_blank" variant="secondary">
            <FormattedMessage defaultMessage="Terms of Service" />
          </Link>
        </div>
      </div>
      <div className={styles.form}>
        <PaymentForm
          coupon={coupon}
          currencyCode={currencyCode}
          cvv={cvv}
          cvvError={cvvError}
          errors={errors}
          expiration={expiration}
          expirationError={expirationError}
          name={name}
          nameError={nameError}
          number={number}
          numberError={numberError}
          plan={plan}
          postalCode={postalCode}
          postalCodeError={postalCodeError}
          recurlyPublicKey={recurlyPublicKey}
          setCvv={setCvv}
          setCvvError={setCvvError}
          setErrors={setErrors}
          setExpiration={setExpiration}
          setExpirationError={setExpirationError}
          setName={setName}
          setNameError={setNameError}
          setNumber={setNumber}
          setNumberError={setNumberError}
          setPostalCode={setPostalCode}
          setPostalCodeError={setPostalCodeError}
          userId={userId}
        />
      </div>
      <div className={styles.button}>
        <Button
          className={styles.button}
          loading={submitting}
          onClick={() => {
            logProPurchaseBuyNowClicked({
              purchase_screen_type_plan: getPurchaseScreenTypePlan(plan, coupon),
              web_purchase_location: WebProPurchaseLocation.ProPage
            });
            track('Pro Purchase Buy Now Clicked');
          }}
          testId="submit"
          text={buttonText}
          type="submit"
          variant="primary"
        />
      </div>
      <div className={styles.fine}>
        <PaymentFinePrint coupon={coupon} currencyCode={currencyCode} isCouponApplied={Boolean(coupon)} plan={plan} />
      </div>
    </form>
  );
}

type CheckoutProps = {
  annualPlan: Plan;
  coupon?: Coupon;
  currencyCode: CurrencyCode;
  initialPlan: Plan;
  recurlyPublicKey: string;
  userId: number;
};

export default function Checkout({ annualPlan, coupon: initialCoupon, currencyCode, initialPlan, recurlyPublicKey, userId }: CheckoutProps) {
  const formatter = useCurrencyFormatter(currencyCode);

  const [coupon, setCoupon] = useState<Coupon>(initialCoupon);
  const [plan, setPlan] = useState(initialPlan);

  function setPlanToAnnual() {
    setPlan(annualPlan);
  }

  useEffect(() => {
    logProCheckoutPageViewed();
  }, []);

  const annualCost = formatter.format(Number(plan.priceTotal));

  // Mobile: https://app.zeplin.io/project/627c18d6c6189e2a52ce6566/screen/627c24f591c20f2d3669a8ea
  // Desktop: https://www.figma.com/file/vJ30CAAm8qdbDO0j5C4Ce7/Web-Pro-Purchase-Flow?node-id=685%3A35997
  // Desktop: https://app.zeplin.io/project/627c18d6c6189e2a52ce6566/screen/627c238b04a41125e91d0422
  if (coupon) {
    const percent = coupon.discountPercentage / 100;

    return (
      <CheckoutBase
        billedSubheading={<FormattedMessage defaultMessage="Discount" />}
        billedSubheadingRight={<FormattedMessage defaultMessage="Save {percent, number, ::percent}" values={{ percent }} />}
        billedToday={
          <>
            <s className={styles.s}>{formatter.format(Number(plan.priceTotal))}</s> {formatter.format(getPriceTotal(plan, coupon))}
          </>
        }
        buttonText={<FormattedMessage defaultMessage="Save today" />}
        coupon={coupon}
        currencyCode={currencyCode}
        heading={<FormattedMessage defaultMessage="Save {percent, number, ::percent} on AllTrails Pro" values={{ percent }} />}
        plan={plan}
        recurlyPublicKey={recurlyPublicKey}
        setCoupon={setCoupon}
        setPlanToAnnual={setPlanToAnnual}
        userId={userId}
      />
    );
  }

  if (isTrial(plan)) {
    return (
      <CheckoutBase
        billedSubheading={<FormattedMessage defaultMessage="Billed after trial" />}
        billedSubheadingRight={<FormattedMessage defaultMessage="{annualCost} annually" values={{ annualCost }} />}
        billedToday={formatter.format(0)}
        buttonText={<FormattedMessage defaultMessage="Start your free trial" />}
        currencyCode={currencyCode}
        heading={<FormattedMessage defaultMessage="Try Pro free for 7 days" />}
        plan={plan}
        recurlyPublicKey={recurlyPublicKey}
        setCoupon={setCoupon}
        setPlanToAnnual={setPlanToAnnual}
        subheading={
          <FormattedMessage defaultMessage="Then just {annualCost} a year after your trial. No commitment, cancel anytime." values={{ annualCost }} />
        }
        userId={userId}
      />
    );
  }

  // Desktop: https://app.zeplin.io/project/627c18d6c6189e2a52ce6566/screen/627c23891dd05a2a732656a0
  // Mobile: https://app.zeplin.io/project/627c18d6c6189e2a52ce6566/screen/627c24a858c4c42d0bd73f80
  return (
    <CheckoutBase
      billedToday={annualCost}
      buttonText={<FormattedMessage defaultMessage="Get Pro" />}
      currencyCode={currencyCode}
      heading={<FormattedMessage defaultMessage="Unlock the full AllTrails experience" />}
      plan={plan}
      recurlyPublicKey={recurlyPublicKey}
      setCoupon={setCoupon}
      setPlanToAnnual={setPlanToAnnual}
      userId={userId}
    />
  );
}
